import PropTypes from "prop-types"
import React, { useRef } from "react"
import styled from "styled-components"
import { documentToReactComponents } from "@contentful/rich-text-react-renderer"
import Carousel from "react-elastic-carousel"
import Container from "./container"
import Banner from "./banner"
import GoogleMap from "./google-map"
import ContactForm from "./contact-form"
import Intro from "./intro"
import GatsbyImage from "./gatsby-image"

const StyledContainer = styled(Container)`
  position: relative;
  z-index: 0;
`

const StyledContent = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: 2rem;

  @media (min-width: 769px) {
    flex-direction: row;
    padding-bottom: 5rem;
  }

  &:last-child {
    padding-bottom: 0 !important;
  }
`

const StyledContentData = styled.div`
  font-size: 1.2rem;
  line-height: 1.4rem;
  text-align: ${props => (props.isTextCentered ? "center" : "left")};

  @media (min-width: 769px) {
    width: ${props => (props.hasContentImage ? "65%" : "100%")};
    font-size: 1.8rem;
    line-height: 2.2rem;
  }

  ${props =>
    props.isContactUs &&
    `
    a, b {
      font-family: ${props.theme.crockpot.secondFont};
      font-size: 1.5rem;
      font-weight: normal;

      @media (min-width: 769px) {
        font-size: 1.9rem;
      }
    }

    b {
      text-transform: uppercase;
    }

    a b{
      font-weight: normal;
    }
  `}

  a {
    color: ${props => props.theme.crockpot.text};
    text-decoration: none;
  }

  h2 {
    font-size: 1.9rem;
    font-family: ${props => props.theme.crockpot.secondFont};
    text-transform: uppercase;
    line-height: 2.3rem;
  }

  p {
    margin-bottom: 1rem;

    @media (min-width: 769px) {
      margin-bottom: 1.45rem;
    }
  }

  i {
    display: inline-block;
    position: relative;
    top: -0.5rem;
    font-style: normal !important;
    font-size: 0.8rem;

    @media (min-width: 769px) {
      font-size: 1.2rem;
    }
  }
`

const StyledContentImage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  > div {
    height: 22rem;
  }

  @media (min-width: 769px) {
    width: 35%;
    padding: 0 2rem;
  }

  img {
    object-fit: contain !important;
  }
`

const Page = ({
  children,
  page = {},
  backLink,
  isTextCentered = false,
  isContactUs = false,
  isFullWidth = false,
  customBannerHeight = null,
}) => {
  const carouselRef = useRef(null)
  let resetTimeout

  return (
    <>
      {page.banner && page.secondBanner && (
        <Carousel
          ref={carouselRef}
          pagination={false}
          showArrows={false}
          enableSwipe={false}
          enableAutoPlay={true}
          autoPlaySpeed={6000}
          onNextEnd={() => {
            clearTimeout(resetTimeout)
            resetTimeout = setTimeout(() => {
              carouselRef?.current?.goTo(0)
            }, 6000)
          }}
        >
          {page.banner && (
            <Banner
              link={page.bannerUrl}
              bannerFixed={page.bannerFixed}
              image={page.banner.gatsbyImageData}
            />
          )}
          {page.secondBanner && (
            <Banner
              link={page.secondBannerUrl}
              bannerFixed={page.bannerFixed}
              image={page.secondBanner.gatsbyImageData}
            />
          )}
        </Carousel>
      )}
      {page.banner && !page.secondBanner && (
        <Banner
          customBannerHeight={page.bannerFixed ? "auto" : customBannerHeight}
          bannerFixed={page.bannerFixed}
          imagePosition={page.bannerPosition}
          link={page.bannerUrl}
          image={page.banner.gatsbyImageData}
        />
      )}

      <StyledContainer hasBottomMargin={true} isFullWidth={isFullWidth}>
        {backLink}
        {page.intro && (
          <Intro
            title={documentToReactComponents(JSON.parse(page.intro.raw))}
          />
        )}
        {children}
        {page.content && (
          <StyledContent>
            <StyledContentData
              hasContentImage={page.contentImage}
              isTextCentered={isTextCentered}
              isContactUs={isContactUs}
            >
              {documentToReactComponents(JSON.parse(page.content.raw))}
            </StyledContentData>
            {page.contentImage && (
              <StyledContentImage>
                <GatsbyImage image={page.contentImage.gatsbyImageData} />
              </StyledContentImage>
            )}
          </StyledContent>
        )}
        {page.secondContent && (
          <StyledContent>
            <StyledContentData
              hasContentImage={page.secondContentImage}
              isTextCentered={isTextCentered}
              isContactUs={isContactUs}
            >
              {documentToReactComponents(JSON.parse(page.secondContent.raw))}
            </StyledContentData>
            {page.secondContentImage && (
              <StyledContentImage>
                <GatsbyImage image={page.secondContentImage.gatsbyImageData} />
              </StyledContentImage>
            )}
          </StyledContent>
        )}
        {page.thirdContent && (
          <StyledContent>
            <StyledContentData
              hasContentImage={page.thirdContentImage}
              isTextCentered={isTextCentered}
              isContactUs={isContactUs}
            >
              {documentToReactComponents(JSON.parse(page.thirdContent.raw))}
            </StyledContentData>
            {page.thirdContentImage && (
              <StyledContentImage>
                <GatsbyImage image={page.thirdContentImage.gatsbyImageData} />
              </StyledContentImage>
            )}
          </StyledContent>
        )}
        {page.hasContactForm && (
          <ContactForm contactFormText={page.contactFormText} />
        )}
        {page.hints && (
          <Banner image={page.hints.gatsbyImageData} link={page.hintsUrl} />
        )}
        {page.googleMaps && (
          <GoogleMap lat={page.googleMaps.lat} lon={page.googleMaps.lon} />
        )}
      </StyledContainer>
    </>
  )
}

Page.propTypes = {
  page: PropTypes.object,
  isTextCentered: PropTypes.bool,
  isContactUs: PropTypes.bool,
  isFullWidth: PropTypes.bool,
  customBannerHeight: PropTypes.string,
}

export default Page
